/**
 * RunStreamInit
 *
 * App-level component that reconnects to an in-progress test run
 * after page refresh. Rendered once in App.js alongside other
 * global listeners (BackgroundChatListener, GlobalToastNotifications).
 *
 * On mount, waits for runStore hydration then checks if there is
 * an active run that needs reconnection.
 */

import { useEffect, useRef } from 'react';
import { useRunStore } from '../../store/automation/runStore';
import { runStreamService } from '../workers/RunStreamService';
import { useUser } from '../../features/auth/hooks/useUser';
import logger from '../services/logging/logger';

const RunStreamInit = () => {
  const hasInit = useRef(false);
  const { userData } = useUser();

  useEffect(() => {
    if (hasInit.current) return;

    // Wait for Zustand persist hydration to complete
    const unsub = useRunStore.persist.onFinishHydration(() => {
      hasInit.current = true;
      const { activeRunMeta } = useRunStore.getState();

      if (activeRunMeta) {
        logger.info('RunStreamInit: found active run, attempting reconnection', {
          runId: activeRunMeta.runId,
        });
        runStreamService.checkAndReconnect(userData);
      }
    });

    // If already hydrated (sync storage), check immediately
    if (useRunStore.persist.hasHydrated()) {
      hasInit.current = true;
      const { activeRunMeta } = useRunStore.getState();

      if (activeRunMeta) {
        logger.info('RunStreamInit: found active run (already hydrated), attempting reconnection', {
          runId: activeRunMeta.runId,
        });
        runStreamService.checkAndReconnect(userData);
      }
    }

    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [userData]);

  return null; // Renders nothing — side-effect only
};

export default RunStreamInit;
